import { CSSProperties, useEffect, useId, useRef, useState } from 'react';
import { Swiper } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Pagination, Navigation } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types/swiper-options';
import { Box } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { useStyles } from './styles';
import { ISwiperWrapperProps } from './interfaces';
import {
  BREAKPOINTS,
  NAVIGATION_ALIGN_ATTRIBUTE,
  NAVIGATION_ALIGN_HEIGHT_PROPERTY,
} from './constants';

/**
 * Returns how many slides one arrow click advances for a given `slidesPerView`.
 *
 * @param slidesPerView - Slides visible at once; `'auto'` falls back to a single slide.
 */
const getSlidesPerGroup = (slidesPerView: SwiperOptions['slidesPerView']) =>
  typeof slidesPerView === 'number' ? Math.floor(slidesPerView) : 1;

/**
 * Copies breakpoints with `slidesPerGroup` matching their `slidesPerView`, so
 * the arrows page through the list instead of moving it by a single slide —
 * swiper only groups slides when the option is set for every breakpoint.
 *
 * @param breakpoints - Swiper breakpoints to derive the paged variant from.
 */
const getPagedBreakpoints = (
  breakpoints: NonNullable<ISwiperWrapperProps['breakpoints']>,
) =>
  Object.entries(breakpoints).reduce(
    (paged, [width, options]) => ({
      ...paged,
      [width]: {
        ...options,
        slidesPerGroup: getSlidesPerGroup(options.slidesPerView),
      },
    }),
    {},
  );

/**
 * Renders children as a swiper slider, paginated with bullets by default.
 *
 * @param props.children - `SwiperSlide` elements to slide through.
 * @param props.horizontalNavigation - Adds prev/next arrows on the sides of the
 * slider, next to the bullets: they page through the list and hide themselves when
 * every slide fits, as swiper does with the bullets.
 * @param props.slidesPerView - Slides visible at once below the smallest breakpoint.
 * @param props.breakpoints - Per-width swiper options; defaults to `BREAKPOINTS`.
 * Arrows line up with the element a slide marks with `NAVIGATION_ALIGN_ATTRIBUTE`
 * (the preview, not the whole card), and fall back to the row's centre.
 *
 * @param props.prevButtonTestId - `data-testid` of the prev arrow, with `horizontalNavigation`.
 * @param props.nextButtonTestId - `data-testid` of the next arrow, with `horizontalNavigation`.
 * @param props.paginationGap - Gap in px between the slides and the bullets. Set it
 * where a slide carries controls of its own along its bottom edge, which the bullets
 * would otherwise overlay; unset keeps swiper's own placement.
 */
const SwiperWrapper = ({
  children,
  horizontalNavigation,
  slidesPerView = 1,
  breakpoints = BREAKPOINTS,
  prevButtonTestId,
  nextButtonTestId,
  paginationGap,
}: ISwiperWrapperProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  // Height of the part of a slide the arrows line up with, 0 while there is
  // nothing to measure — then they stay centred on the whole row.
  const [alignHeight, setAlignHeight] = useState(0);
  const classes = useStyles(paginationGap);

  // A slide is taller than its preview (the name sits underneath), so centring
  // the arrows on the row would drop them below the previews. The element to
  // line up with is marked by the slide itself and measured live: it is square,
  // so its height follows the panel width, and slides only arrive after the
  // first render. Watching the row instead would not do — a shorter preview
  // leaves the row's own box unchanged.
  useEffect(() => {
    const root = rootRef.current;

    if (!horizontalNavigation || !root) {
      return;
    }

    let observed: Element | null = null;

    const measure = () => {
      const target = root.querySelector(`[${NAVIGATION_ALIGN_ATTRIBUTE}]`);

      if (target !== observed) {
        if (observed) {
          resizeObserver.unobserve(observed);
        }

        if (target) {
          resizeObserver.observe(target);
        }

        observed = target;
      }

      setAlignHeight(target ? target.getBoundingClientRect().height : 0);
    };

    const resizeObserver = new ResizeObserver(measure);
    // Slides are replaced on every refetch, so the measured element has to be
    // picked up again. Class and style writes are attributes, not children, so
    // swiper's own updates and this effect's output do not retrigger it.
    const mutationObserver = new MutationObserver(measure);

    measure();
    mutationObserver.observe(root, { childList: true, subtree: true });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [horizontalNavigation]);

  // Inline, not in `sx`: swiper keeps its state classes on these elements, so
  // their class name has to stay the same across renders.
  const navigationAlignStyle = alignHeight
    ? ({
        alignSelf: 'flex-start',
        [NAVIGATION_ALIGN_HEIGHT_PROPERTY]: `${alignHeight}px`,
      } as CSSProperties)
    : undefined;

  // Swiper resolves the arrows itself, from selectors unique to this
  // wrapper — refs would not do: react attaches the ref of a sibling
  // rendered after the slider only after the slider has initialized, so
  // the next arrow would stay unbound. Instance-scoped classes also keep
  // several sliders on one page from grabbing each other's arrows.
  const instanceId = useId().replace(/:/g, '');
  const prevButtonClass = `swiper-nav-prev-${instanceId}`;
  const nextButtonClass = `swiper-nav-next-${instanceId}`;

  const swiperNode = (
    <Swiper
      slidesPerView={slidesPerView}
      slidesPerGroup={
        horizontalNavigation ? getSlidesPerGroup(slidesPerView) : undefined
      }
      spaceBetween={10}
      pagination={{ clickable: true }}
      navigation={
        horizontalNavigation
          ? { prevEl: `.${prevButtonClass}`, nextEl: `.${nextButtonClass}` }
          : false
      }
      breakpoints={
        horizontalNavigation ? getPagedBreakpoints(breakpoints) : breakpoints
      }
      modules={horizontalNavigation ? [Navigation, Pagination] : [Pagination]}
    >
      {children}
    </Swiper>
  );

  if (!horizontalNavigation) {
    return <Box sx={classes.swiperWrap}>{swiperNode}</Box>;
  }

  return (
    <Box
      ref={rootRef}
      sx={{ ...classes.swiperWrap, ...classes.navigationWrap }}
    >
      <Box
        className={prevButtonClass}
        sx={classes.navigationBtn}
        style={navigationAlignStyle}
        data-testid={prevButtonTestId}
      >
        <ChevronLeftIcon />
      </Box>
      <Box sx={classes.swiperContainer}>{swiperNode}</Box>
      <Box
        className={nextButtonClass}
        sx={classes.navigationBtn}
        style={navigationAlignStyle}
        data-testid={nextButtonTestId}
      >
        <ChevronRightIcon />
      </Box>
    </Box>
  );
};

export default SwiperWrapper;
